import { WidgetPluginComponentData } from '@wix/ambassador-app-service-webapp/types';
import { DocumentServicesObject, CompRef } from '@wix/document-services-types';
import {
  WidgetPointer,
  PluginActionContext,
  PluginsEditorType,
  PluginInstallationError,
} from '@wix/editor-platform-host-integration-apis';
import { HttpClient } from '@wix/http-client';
import { LogObject } from '@wix/bi-logger-editor-x/v2/types';
import type { PluginPlacement } from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';
import { PluginInfo } from '../../../../types/widgetPlugins';
import { reportBi } from '../../../../essentials';
import { ComponentPreviewState } from '../constants';
import { createDependenciesDriver } from '../../appDependenciesDriver';
import { getEditorInfo } from '../../../../utils';
import { getAppDataByCompRef } from './getAppData';
import { PluginActionFullContext } from '../types';

export function getEditorHostForPlugins(): PluginsEditorType {
  const host = getEditorInfo()?.host;
  const editorName = host?.toLowerCase();
  switch (editorName) {
    case 'editor':
      return PluginsEditorType.Classic;
    case 'editor_x':
      return PluginsEditorType.Responsive;
    case 'studio':
      return PluginsEditorType.Studio;
    case 'blocks':
      return PluginsEditorType.Blocks;
    case 'studio2':
    case 'editor3':
      return PluginsEditorType.Editor3;
    default:
      throw new Error(`Unsupported editor type for plugins: ${editorName}`);
  }
}

export function extendPluginActionContext(
  context: PluginActionContext,
): PluginActionFullContext {
  const editorName = getEditorHostForPlugins();
  const editorKind =
    editorName === PluginsEditorType.Blocks
      ? 'applicationEditor'
      : 'siteEditor';

  return {
    ...context,
    editorName,
    editorKind,
  };
}

export function getPluginMarketData(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
): WidgetPluginComponentData | undefined {
  const pluginMarketDataComponent = documentServicesAPI.tpa.app
    .getDataByAppDefId(widgetPointer.appDefinitionId)
    ?.components?.find(
      (component) =>
        (component?.data as WidgetPluginComponentData)?.referenceComponentId ===
        widgetPointer.widgetId,
    );
  return pluginMarketDataComponent?.data as WidgetPluginComponentData;
}

export function getPluginInfoByCompRef(
  documentServicesAPI: DocumentServicesObject,
  pluginCompRef: CompRef,
): PluginInfo | undefined {
  const { appDefinitionId, widgetId } = getAppDataByCompRef(
    documentServicesAPI,
    pluginCompRef,
  );
  return {
    ...getPluginMarketData(documentServicesAPI, {
      appDefinitionId,
      widgetId,
    })?.marketData,
    widgetId,
    appDefinitionId,
  } as PluginInfo;
}

export function reportPluginBi(logObject: LogObject<Object>) {
  const { params, ...event } = logObject;
  reportBi(logObject.evid, {
    ...event,
    ...params,
  });
}

export function serializePlacement(placement: PluginPlacement) {
  return (
    placement &&
    `${placement.appDefinitionId}_${placement.widgetId}_${placement.slotId}`
  );
}

export function buildComponentPreviewState(
  slotName: string,
  state: ComponentPreviewState,
) {
  return `${slotName}-${state}`;
}

export function getEditorPreviewFrame(): HTMLIFrameElement {
  return (document.getElementById('preview') || // Classic Editor
    document.getElementsByName('preview-frame')[0]) as HTMLIFrameElement; // Studio Editor
}

/*
 * The functions which used `_DEPRECATED_BAD_PERFORMANCE` suffix are migrated from santa-editor repository.
 * They use deprecated API which are not recommended to use.
 *
 * Instead of:
 * - `documentServicesAPI.components.getContainer`
 * They use:
 * - `documentServicesAPI.deprecatedOldBadPerformanceApis.components.getContainer`
 *
 * The only reason for using deprecated API is to get access to components inside `RefComponent`.
 */
export function getContainer_DEPRECATED_BAD_PERFORMANCE(
  documentServicesAPI: DocumentServicesObject,
  compRef: CompRef,
): CompRef | null {
  return documentServicesAPI.deprecatedOldBadPerformanceApis.components.getContainer(
    compRef,
  );
}

export function isAppProvisioned(
  documentServicesAPI: DocumentServicesObject,
  appDefinitionId: string,
): boolean {
  return (
    documentServicesAPI.platform.isAppActive(appDefinitionId) ||
    documentServicesAPI.tpa.app.isInstalled(appDefinitionId) ||
    documentServicesAPI.platform.isAppProvisioned(appDefinitionId)
  );
}

async function startAppManagerInstallation(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
): Promise<void> {
  const { ClientSDK, Platform } = await import(
    '@wix/installation-manager-client-sdk'
  );
  const metaSiteId = documentServicesAPI.generalInfo.getMetaSiteId();
  const appInstaller = new ClientSDK({
    platform: Platform.EDITOR,
    metaSiteId,
    httpClient: new HttpClient(),
  });
  const withVersion = widgetPointer.version
    ? { version: widgetPointer.version }
    : {};
  return new Promise((resolve, reject) => {
    // opens concent modal
    appInstaller.installAppWithIntent({
      appDefId: widgetPointer.appDefinitionId,
      onInstallationSuccess: () => resolve(),
      onInstallationFail: () => reject(PluginInstallationError.Fail),
      onInstallationCancel: () => reject(PluginInstallationError.ConsentCancel),
      onInstallationWindowClosed: () =>
        reject(PluginInstallationError.ConsentClose),
      intent: 'plugins',
      ...withVersion,
    });
  });
}

export async function installPluginApp(
  documentServicesAPI: DocumentServicesObject,
  widgetPointer: WidgetPointer,
  actionContext: PluginActionContext,
) {
  if (actionContext.controlMode === 'user') {
    await startAppManagerInstallation(documentServicesAPI, widgetPointer);
  }

  const dependencyDriver = await createDependenciesDriver(
    documentServicesAPI,
    widgetPointer.appDefinitionId,
  );
  const appDefIdsToInstall =
    dependencyDriver.getAppsToInstall().appsToInstall || [];
  for (const appDefId of appDefIdsToInstall) {
    await addApp(
      appDefId,
      getVersion(widgetPointer.version),
      documentServicesAPI,
    );
  }
}

function getVersion(versionString: string | undefined) {
  if (!versionString) {
    return 'latest';
  }

  return versionString.startsWith('~') ? versionString : `~${versionString}`;
}

function addApp(
  appDefinitionId: string,
  version: string,
  documentServicesAPI: DocumentServicesObject,
) {
  return new Promise<void>((resolve) => {
    documentServicesAPI.platform.addApps([appDefinitionId], {
      [appDefinitionId]: { headlessInstallation: true, version },
      finishAllCallback: () => {
        resolve();
      },
    });
  });
}

export {
  createWidgetPluginComponentDefinition,
  getResponsiveLayouts,
  getWidgetPluginType,
} from './pluginComponentDefinitions';
export type { SupportedWidgetType } from './pluginComponentDefinitions';
export { getSlotDefinitions, filterPublicSlots } from './slotDefinitions';
export { getBuilderAppData } from './getBuilderAppData';
